/* NAMES, NOT COUNTS.
 *
 * The standing rule: a panel that says "6 members at risk" has hidden the only thing
 * anybody can act on. The Owner dashboard is the single documented exception, and
 * even there every count must click through to the names. This component is where
 * both treatments live, so the rule is kept in one place rather than in twelve. */
import type { Member } from '@acrostic/model';
import { SCORE_AMBER, SCORE_RED } from '@acrostic/model';
import { NoneRow } from './Panel.js';

export function tone(score: number | null | undefined): 'risk' | 'warn' | 'good' {
  if (score == null) return 'good';
  if (score <= SCORE_RED) return 'risk';
  if (score <= SCORE_AMBER) return 'warn';
  return 'good';
}

/** A suppressed member holds NO score. Not a low one, not a stale one (SITE-RULES 1). */
export function scoreOf(m: Member): number | null {
  return m.suppression ? null : m.risk?.score ?? null;
}

export function reasonOf(m: Member): string {
  if (m.suppression) {
    const kind = m.suppression.kind === 'hold' ? 'On hold' : 'On Care Track';
    return m.suppression.reason ? `${kind} · ${m.suppression.reason}` : kind;
  }
  return m.risk?.why?.[0] ?? 'No reason on record';
}

export function PersonRow({ m, onOpen }: { m: Member; onOpen?: (m: Member) => void }) {
  const score = scoreOf(m);
  const Tag = onOpen ? 'button' : 'div';
  return (
    <Tag
      className={'row' + (onOpen ? ' clickable' : '')}
      {...(onOpen ? { type: 'button' as const, onClick: () => onOpen(m) } : {})}
    >
      <span className={'dot ' + tone(score)} aria-hidden="true" />
      <div className="who">
        <div className="n t-label-large">{m.name}</div>
        <div className="s t-body-small">{reasonOf(m)}</div>
      </div>
      <div className="score t-label-medium">
        {score == null ? <span className="muted">Not scored</span> : score}
      </div>
    </Tag>
  );
}

export function PeopleList({ people, empty, onOpen }: {
  people: Member[]; empty: string; onOpen?: (m: Member) => void;
}) {
  if (!people.length) return <NoneRow>{empty}</NoneRow>;
  return (
    <div className="rows">
      {people.map((m) => <PersonRow key={m.id} m={m} {...(onOpen ? { onOpen } : {})} />)}
    </div>
  );
}
